/**
 * वर्तमान सर्वे on the phone (N-PAC, 3 Nov 2026): what the survey screen needs that is not React — checking
 * a filled form, turning it into the answer the API takes, and the small queue of answers not yet sent
 * (kept on the phone so an answer is never lost when the network drops).
 *
 * Plain TypeScript, no browser globals: unit-tested in Node.
 */
import { normalizeDigits } from "@core/boothCode";

export type SurveyMethod = "d2d" | "tele";
export type SurveyStatus = "strong" | "neutral" | "weak";
export const SURVEY_STATUSES: SurveyStatus[] = ["strong", "neutral", "weak"];
export const MAX_PROBLEMS = 10;

/** the answer as the API takes it */
export interface SurveyAnswer {
  clientId: string;
  boothCode: string;
  method: SurveyMethod;
  placeId: number | null;
  placeName: string | null;
  name: string;
  mobile: string;
  age: number | null;
  profession: string | null;
  orgRole: string | null;
  status: SurveyStatus;
  problems: string[];
  suggestion: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  collectedAt: string;
}

/** what the form holds (strings as typed) */
export interface SurveyForm {
  name: string;
  mobile: string;
  age: string;
  profession: string;
  orgRole: string;
  status: SurveyStatus | "";
  problems: string[];
  suggestion: string;
}

export const blankForm = (): SurveyForm => ({ name: "", mobile: "", age: "", profession: "", orgRole: "", status: "", problems: [""], suggestion: "" });

/** "98765 43210", "+91 98765-43210", "०९८७६५४३२१०" → "9876543210"; null when not a 10-digit Indian mobile */
export function normalizeMobile(input: string): string | null {
  let d = normalizeDigits(input).replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return /^[6-9][0-9]{9}$/.test(d) ? d : null;
}

export type FormErrors = Partial<Record<"name" | "mobile" | "age" | "status", "required" | "invalid">>;

/** what is wrong with the form (empty = ready to send) */
export function checkForm(f: SurveyForm): FormErrors {
  const e: FormErrors = {};
  if (!f.name.trim()) e.name = "required";
  else if (f.name.trim().length > 120) e.name = "invalid";
  if (!f.mobile.trim()) e.mobile = "required";
  else if (!normalizeMobile(f.mobile)) e.mobile = "invalid";
  if (f.age.trim()) {
    const a = Number(normalizeDigits(f.age).replace(/\D/g, ""));
    if (!Number.isInteger(a) || a < 15 || a > 120) e.age = "invalid";
  }
  if (!f.status) e.status = "required";
  return e;
}

/** the problems as a clean list: trimmed, no blanks, no repeats, at most MAX_PROBLEMS */
export function cleanProblems(list: readonly string[]): string[] {
  const out: string[] = [];
  for (const p of list) {
    const s = p.trim().slice(0, 200);
    if (s && !out.some((x) => x.toLowerCase() === s.toLowerCase())) out.push(s);
  }
  return out.slice(0, MAX_PROBLEMS);
}

export function buildAnswer(
  f: SurveyForm,
  ctx: { clientId: string; boothCode: string; method: SurveyMethod; placeId: number | null; placeName: string | null; fix: { lat: number; lng: number; accuracyM: number | null } | null; now: number }
): SurveyAnswer {
  const age = f.age.trim() ? Number(normalizeDigits(f.age).replace(/\D/g, "")) : null;
  const gps = ctx.method === "d2d" ? ctx.fix : null;
  return {
    clientId: ctx.clientId,
    boothCode: ctx.boothCode,
    method: ctx.method,
    placeId: ctx.placeId,
    placeName: ctx.placeName?.trim() || null,
    name: f.name.trim().slice(0, 120),
    mobile: normalizeMobile(f.mobile) ?? "",
    age: age != null && Number.isInteger(age) ? age : null,
    profession: f.profession.trim().slice(0, 80) || null,
    orgRole: f.orgRole.trim().slice(0, 160) || null,
    status: (f.status || "neutral") as SurveyStatus,
    problems: cleanProblems(f.problems),
    suggestion: f.suggestion.trim().slice(0, 500) || null,
    lat: gps ? gps.lat : null,
    lng: gps ? gps.lng : null,
    accuracyM: gps?.accuracyM != null ? Math.round(gps.accuracyM) : null,
    collectedAt: new Date(ctx.now).toISOString(),
  };
}

/** where the unsent answers of this login in this assembly are kept */
export const queueKey = (assemblyId: string) => `surveyQueue:${assemblyId}`;

/** The stored queue; anything unreadable is an empty queue (never throws). */
export function readQueue(raw: string | null): SurveyAnswer[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is SurveyAnswer => !!x && typeof x === "object" && typeof (x as SurveyAnswer).clientId === "string" && typeof (x as SurveyAnswer).boothCode === "string" && typeof (x as SurveyAnswer).name === "string");
  } catch {
    return [];
  }
}

/** Add an answer; the same clientId twice stays once. */
export function addAnswer(queue: readonly SurveyAnswer[], a: SurveyAnswer): SurveyAnswer[] {
  return queue.some((q) => q.clientId === a.clientId) ? [...queue] : [...queue, a];
}

/** Drop the answers the server has taken (saved or already there). */
export function dropSent(queue: readonly SurveyAnswer[], clientIds: readonly string[]): SurveyAnswer[] {
  const done = new Set(clientIds);
  return queue.filter((q) => !done.has(q.clientId));
}

/** at most this many answers per request (the API takes 200) */
export const SEND_BATCH = 50;
